import { Component, computed, inject, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BulkDownloadJobItem, buildBulkDownloadFileName } from '../../models/bulk-download-job.model';
import { RealtimeConnection, RealtimeEnvelope } from '@zayan-erp/realtime';
import { BulkDownloadJobStatus } from '../../models/bulk-download-job.model';
import {
  BulkDownloadEvents,
  BulkDownloadFailedPayload,
  BulkDownloadReadyPayload
} from '../../models/bulk-download-events';
import { FilesystemService } from '../../services/file-system.service';

@Component({
  selector: 'app-bulk-download-panel',
  standalone: true,
  imports: [],
  templateUrl: './bulk-download-panel.component.html',
  styleUrl: './bulk-download-panel.component.scss'
})
export class BulkDownloadPanelComponent {
  #filesystem = inject(FilesystemService);
  #realtime = inject(RealtimeConnection);
  #ignored = new Set<string>();

  ready = output<string[]>();
  isOpen = signal(false);
  isCollapsed = signal(false);
  jobs = signal<BulkDownloadJobItem[]>([]);

  buildingCount = computed(() => this.jobs().filter(j => j.status === 'building').length);
  hasFinishedJobs = computed(() => this.jobs().some(j => j.status !== 'building'));

  constructor() {
    this.#realtime.envelopes$.pipe(takeUntilDestroyed()).subscribe(envelope => this.onRealtimeEnvelope(envelope));
  }

  startDownload(fileIds: string[], fileNames: string[], contextName?: string | null): void {
    const fileName = buildBulkDownloadFileName(contextName);

    this.#ensureNotificationsHubReady().then(() => {
      this.#filesystem.createBulkDownloadJob(fileIds).subscribe({
        next: data => {
          const jobId = data?.jobId as string | undefined;
          if (!jobId) return;

          this.#ignored.delete(jobId);
          this.jobs.update(jobs => [
            { jobId, fileIds, fileNames, fileName, status: 'building' },
            ...jobs.filter(j => j.jobId !== jobId)
          ]);
          this.show();
        }
      });
    });
  }

  #ensureNotificationsHubReady(): Promise<void> {
    const realtimeReady = this.#realtime.start().catch(() => undefined);
    const timeout = new Promise<void>(resolve => setTimeout(resolve, 8000));

    return Promise.race([realtimeReady.then(() => undefined), timeout]);
  }

  retry(job: BulkDownloadJobItem): void {
    this.#ignored.add(job.jobId);
    this.jobs.update(jobs => jobs.filter(j => j.jobId !== job.jobId));
    this.startDownload(job.fileIds, job.fileNames);
  }

  download(job: BulkDownloadJobItem): void {
    if (!job.downloadUrl) return;
    const a = Object.assign(document.createElement('a'), {
      href: job.downloadUrl,
      download: job.fileName,
      rel: 'noopener'
    });
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  dismiss(jobId: string): void {
    this.jobs.update(jobs => jobs.filter(j => j.jobId !== jobId));
    if (!this.jobs().length) this.isOpen.set(false);
  }

  cancelAll(): void {
    for (const job of this.jobs()) {
      if (job.status === 'building') this.#ignored.add(job.jobId);
    }
    this.jobs.set([]);
    this.isOpen.set(false);
  }

  clearFinished(): void {
    this.jobs.update(jobs => jobs.filter(j => j.status === 'building'));
    if (!this.jobs().length) this.isOpen.set(false);
  }

  toggleCollapsed(): void {
    this.isCollapsed.update(v => !v);
  }

  fileCountLabel(count: number): string {
    return count === 1 ? '1 File' : `${count} Files`;
  }

  show(): void {
    this.isOpen.set(true);
    this.isCollapsed.set(false);
  }

  onRealtimeEnvelope(envelope: RealtimeEnvelope): void {
    switch (envelope.type) {
      case BulkDownloadEvents.ready: {
        const payload = envelope.payload as BulkDownloadReadyPayload | undefined;
        return this.#settle(payload?.jobId, 'ready', payload?.downloadUrl);
      }
      case BulkDownloadEvents.failed: {
        const payload = envelope.payload as BulkDownloadFailedPayload | undefined;
        return this.#settle(payload?.jobId, 'failed');
      }
      default:
        return;
    }
  }

  /** Terminal update for one job. Jobs this panel never started, or the user cancelled, are ignored. */
  #settle(jobId: string | undefined, status: BulkDownloadJobStatus, downloadUrl?: string): void {
    const job = this.jobs().find(j => j.jobId === jobId);
    if (!jobId || this.#ignored.has(jobId) || !job) return;

    this.jobs.update(jobs => jobs.map(j => (j.jobId === jobId ? { ...j, status, downloadUrl } : j)));
    this.show();

    if (status === 'ready') {
      this.ready.emit(job.fileIds);
    }
  }
}
